import { useEffect, useState } from "react";
import { api, mediaUrl } from "../lib/api";
import { Seo } from "../lib/seo";
import { CourseCard } from "../components/CourseCard";
import { HomeArticleCarousel } from "../components/HomeArticleCarousel";
import { useLeadDialog } from "../context/LeadDialogContext";
import { useSettings } from "../context/SettingsContext";
import { useLang } from "../context/LanguageContext";
import { Reveal } from "../components/Reveal";
import { Loader2, ArrowDown } from "lucide-react";

export default function HomePage() {
  const [courses, setCourses] = useState(null);
  const lead = useLeadDialog();
  const s = useSettings();
  const { t, lang } = useLang();

  useEffect(() => {
    setCourses(null);
    api.get("/courses", { params: { lang } }).then((r) => setCourses(r.data)).catch(() => setCourses([]));
  }, [lang]);

  return (
    <div className="bg-[#F4F8FF]">
      <Seo
        title="Advanced Post Graduate Excellence | Corsi di formazione odontoiatrica"
        description="Corsi pratici di formazione clinica per odontoiatri: implantologia, endodonzia, parodontologia. Docenti di riferimento, casi reali, posti limitati."
        canonical={typeof window !== "undefined" ? window.location.origin + "/" : ""}
      />
      {/* HERO — dark, high-contrast, intense azure */}
      <section data-testid="home-hero" className="relative isolate overflow-hidden bg-[#0B1220]">
        <div className="grid-overlay absolute inset-0 pointer-events-none" />
        <div className="absolute -top-32 -right-24 h-[640px] w-[640px] rounded-full bg-[#1452F0]/30 blur-[140px] pointer-events-none" />
        <div className="relative px-6 md:px-12 max-w-[1400px] mx-auto pt-16 md:pt-24 pb-20 md:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-6 fade-up">
              <span className="text-xs font-semibold uppercase tracking-[0.28em] text-[#5B9DFF]">{s.home_overline}</span>
              <h1 className="font-heading font-light tracking-tight leading-[0.92] mt-5 text-5xl sm:text-6xl lg:text-7xl silver-text whitespace-pre-line">
                {s.home_title}
              </h1>
              <p className="mt-7 text-base md:text-lg font-light leading-relaxed text-[#9AA7BD] max-w-xl">
                {s.home_subtitle}
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-4">
                <a
                  href="#corsi"
                  data-testid="home-explore-btn"
                  className="group bg-[#1452F0] text-white hover:bg-[#0E3DC0] transition-colors rounded-sm px-8 py-4 text-sm font-medium tracking-wide flex items-center justify-center gap-2 shadow-[0_0_34px_rgba(20,82,240,0.5)]"
                >
                  {s.home_cta_primary}
                  <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
                </a>
                <button
                  data-testid="home-info-btn"
                  onClick={() => lead?.openLead(null, "homepage_hero")}
                  className="border border-white/30 text-white hover:bg-white hover:text-[#0B1220] transition-colors rounded-sm px-8 py-4 text-sm font-medium tracking-wide"
                >
                  {s.home_cta_secondary}
                </button>
              </div>
            </div>
            <div className="lg:col-span-6 fade-up" style={{ animationDelay: "0.1s" }}>
              <div className="overflow-hidden rounded-sm border border-white/10 glow-azure">
                <img src={mediaUrl(s.home_hero_image)} alt="Formazione odontoiatrica" className="w-full h-[360px] lg:h-[560px] object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COURSE GRID */}
      <section id="corsi" data-testid="home-courses" className="px-6 md:px-12 max-w-[1400px] mx-auto py-24">
        <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
          <div>
            <span className="overline">{s.home_courses_overline}</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#0B1220] mt-3">
              {s.home_courses_title}
            </h2>
          </div>
          <p className="text-[#5C6678] font-light max-w-sm md:text-right">
            {s.home_courses_note}
          </p>
        </Reveal>

        {courses === null ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-[#1452F0]" />
          </div>
        ) : courses.length === 0 ? (
          <div data-testid="home-empty" className="py-20 text-center">
            <p className="font-heading text-2xl text-[#0B1220]">{t("home.empty_title")}</p>
            <p className="mt-2 text-[#5C6678]">{t("home.empty_sub")}</p>
            <button
              onClick={() => lead?.openLead(null, "homepage_empty")}
              className="mt-6 bg-[#1452F0] text-white rounded-sm px-7 py-3.5 text-sm font-medium shadow-[0_0_30px_rgba(20,82,240,0.4)]"
            >
              {t("common.request_info")}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {courses.map((c, i) => (
              <CourseCard key={c.id} course={c} index={i} />
            ))}
          </div>
        )}
      </section>

      <HomeArticleCarousel />
    </div>
  );
}
